<template>
  <section-card
    data-test="my-env-section-charts"
    :data-item-count="displayed.length"
    :title="embedded ? t('mijn_omgeving.charts.title') : null"
    :subtitle="embedded ? t('mijn_omgeving.charts.subtitle') : null"
    :is-loading="!isLoaded && !hasItems"
    :is-empty="isLoaded && !hasItems"
    :empty-title="t('mijn_omgeving.charts.empty_title')"
    :empty-message="t('mijn_omgeving.charts.empty_message')"
  >
    <!-- PI-321: Favorites aren't "created" here — they appear when the user
         stars a chart. The old "+ Nieuw" CTA routed into the dashboard-builder
         marketplace (wrong target), so it's replaced by an info hint. -->
    <template v-if="embedded" #action>
      <app-tooltip :content="t('mijn_omgeving.charts.favorites_hint')" placement="left">
        <icon
          :path="mdiInformationOutline"
          size="16"
          class="text-theme-text-muted"
          tabindex="0"
          :aria-label="t('mijn_omgeving.charts.favorites_hint')"
        />
      </app-tooltip>
    </template>

    <!-- Skeleton: 2 ghost chart cards (snapshot) or 3 (full mode) sized to
         match MiniGraphVisualization: title row + sparkline area + 2 footer
         rows. Prevents the snapshot column from collapsing while the
         profile + chart data are in flight. -->
    <template #loadingSkeleton>
      <div
        :class="[
          'grid gap-3',
          snapshot ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        ]"
        aria-hidden="true"
      >
        <div
          v-for="i in snapshot ? 2 : 3"
          :key="`chart-skel-${i}`"
          class="rounded-theme-md border p-3 space-y-2
            bg-theme-surface border-theme-border dark:bg-theme-surface dark:border-theme-border"
        >
          <div class="flex items-center justify-between gap-2">
            <skeleton-text width="55%" height="13px" />
            <skeleton-text width="14px" height="14px" variant="circle" />
          </div>
          <skeleton-text
            width="100%"
            :height="snapshot ? '48px' : '64px'"
            variant="rectangle"
          />
          <div class="space-y-1 pt-1">
            <skeleton-text width="30%" height="9px" />
            <div class="flex items-center justify-between gap-2">
              <skeleton-text width="60%" height="11px" />
              <skeleton-text width="40px" height="11px" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Standalone /my-environment/charts route (`embedded=false`):
         render the REAL DatasetChart for each favourite via DashboardPanel,
         laid out half-width on mid screens and third-width on wide screens.
         Mini sparklines stay reserved for the Home snapshot and the
         my-environment overview, where we're competing with other widgets
         for vertical space. -->
    <div
      v-if="!embedded"
      class="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
    >
      <div
        v-for="(chart, idx) in displayed"
        :key="chart.id || idx"
        class="relative h-[28rem]"
      >
        <dashboard-panel
          v-if="chart.dataset_id && chart.visualization_id"
          :panel="buildFullPanel(chart)"
        />
        <chart-card-tile
          v-else
          :title="chart.label"
          :subtitle="chart.id"
        />
      </div>
    </div>

    <!-- Embedded mode (Home snapshot + my-environment overview): mini
         sparklines in a tight 1/2/3-column strip. The card itself is the
         link; the arrow inside MiniGraphVisualization is decorative. -->
    <div
      v-else
      :class="[
        'grid gap-3',
        snapshot
          ? 'grid-cols-1'
          : limit && limit < 3
            ? 'grid-cols-1 sm:grid-cols-2'
            : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      ]"
    >
      <component
        :is="chart.resolved_deep_link ? 'router-link' : 'article'"
        v-for="(chart, idx) in displayed"
        :key="chart.id || idx"
        :to="chart.resolved_deep_link || undefined"
        class="block rounded-theme-md border p-3 transition-colors
          bg-theme-surface border-theme-border hover:border-theme-palette-emerald/40 hover:bg-theme-palette-emerald/30
          dark:bg-theme-surface dark:border-theme-border
          dark:hover:border-theme-palette-emerald dark:hover:bg-theme-palette-emerald/10"
      >
        <mini-graph-visualization
          v-if="chart.dataset_id && chart.visualization_id"
          :dataset-id="chart.dataset_id"
          :visualization-id="chart.visualization_id"
          :title="chart.label"
          :deep-link="chart.resolved_deep_link || ''"
          :height="snapshot ? 48 : 64"
        />
        <chart-card-tile
          v-else
          :title="chart.label"
          :subtitle="chart.id"
        />
      </component>
    </div>
  </section-card>
</template>

<script setup>
  /* eslint-disable no-undef */
  import DashboardPanel from '@/features/custom-dashboards/components/DashboardPanel.vue';
  import Icon from '@/components/Icon';
  import AppTooltip from '@/components/ui/feedback/AppTooltip.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import ChartCardTile from '@/components/ui/data-display/ChartCardTile.vue';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import MiniGraphVisualization from '@/components/visualizations/graph/MiniGraphVisualization.vue';
  import { useProfile } from '@/composables/profile';
  import { resolveVisualizationDeepLink } from '@/features/datasets/domain/datasetUsageRegistry';
  import { mdiInformationOutline } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    limit: { type: Number, default: null },
    /** When true, force a single-column compact strip — used by the Home Layer 3 snapshot. */
    snapshot: { type: Boolean, default: false },
    /** When `false`, the section-card hides its title — used by the
     *  standalone `/my-environment/charts` route where the page-level
     *  StandardToolPage header already identifies the section. */
    embedded: { type: Boolean, default: true },
  });

  const { profile, isLoaded } = useProfile();
  const { t } = useI18n();

  const suggestions = computed(() => profile.value.suggested_charts || []);
  // Resolve the click-through target on the fly. Priority:
  //   1. A dashboard that already embeds this exact (dataset, viz) pair,
  //      with `?highlight={panelId}` so the dashboard scrolls/flashes the
  //      matching panel into view (chart-sync controller honours this).
  //   2. The fullscreen single-viz route — chart in context, no chrome.
  //   3. The preset's stored `deep_link` (often a category page) as a
  //      last-resort fallback when neither of the above applies.
  // This guarantees clicking a sparkline always lands the user on the
  // actual chart rather than a generic dataset landing page.
  const displayed = computed(() => {
    const arr = props.limit
      ? suggestions.value.slice(0, props.limit)
      : suggestions.value;
    return arr.map((chart) => ({
      ...chart,
      resolved_deep_link:
        resolveVisualizationDeepLink(chart.dataset_id, chart.visualization_id) ||
        chart.deep_link ||
        '',
    }));
  });
  const hasItems = computed(() => displayed.value.length > 0);

  // Build the panel descriptor DashboardPanel expects. Same shape as the
  // dataset landing page's `buildPanel` — that's the canonical way to host
  // a full visualization outside a real dashboard.
  function buildFullPanel(chart) {
    return {
      id: `my-charts-${chart.dataset_id}-${chart.visualization_id}`,
      type: 'datasetChart',
      datasetId: chart.dataset_id,
      visualizationId: chart.visualization_id,
      visualizationType: chart.visualization_type || '',
    };
  }
</script>
